import type {
  GoogleMapInstance,
  GoogleMapsApi,
  LatLngLiteral,
  OverlayViewBase,
} from '@/shared/google/googleMaps'
import { placeLabels } from '../../domain/map/civicNumber'

// Repris (adapt) de reca-app-v3 `src/features/markers/infrastructure/google/fieldMapLayer.ts`. Ici la couleur dit
// l'état du travail de la mission (jaune à faire, rouge problème), jamais la couleur permanente de la route.
// Une résidence terminée n'a plus de pastille. La résidence active du moteur est blanche, une candidate en approche
// ou en choix d'adresse est cerclée en pointillé. Les cercles techniques (capture, travail, sortie) restent dans
// Routes et le laboratoire : ils ne s'affichent pas ici.

interface FieldPoint {
  id: string
  x: number
  y: number
}

interface FieldCluster {
  ids: string[]
  x: number
  y: number
}

/** Regroupe les points écran distants de moins de `minDistance` px (glouton, ordre stable). */
export function clusterPoints(points: FieldPoint[], minDistance: number): FieldCluster[] {
  const clusters: FieldCluster[] = []
  for (const point of points) {
    const near = clusters.find((cluster) => Math.hypot(cluster.x - point.x, cluster.y - point.y) < minDistance)
    if (near) {
      const total = near.ids.length
      near.x = (near.x * total + point.x) / (total + 1)
      near.y = (near.y * total + point.y) / (total + 1)
      near.ids.push(point.id)
    } else {
      clusters.push({ ids: [point.id], x: point.x, y: point.y })
    }
  }
  return clusters
}

const colors = {
  surface: '#121826',
  text: '#F2F4F8',
  secondary: '#C3CAD6',
  problem: '#E63947',
  waiting: '#F5B82E',
  operator: '#1A66D9',
}

export type MarkerEmphasis = 'ACTIVE' | 'APPROACH' | 'CANDIDATE' | null

export interface FieldMapMarker {
  id: string
  position: LatLngLiteral
  address: string
  name: string
  status: 'WAITING' | 'PROBLEM'
  /** État de détection (moteur) : active, en approche, candidate d'un choix d'adresse. */
  emphasis: MarkerEmphasis
  /** Étiquette d'état courte (« en cours », « en approche », « ? »). */
  chip: string | null
  /** Numéro civique RECA (suffixe conservé), affiché en grand; `null` : aucun numéro lisible. */
  civic?: string | null
  /** Ordre de la route : priorité des étiquettes après l'active et la sélectionnée. */
  order?: number
}

export interface MarkerAppearance {
  fill: string
  ring: 'solid' | 'dashed'
  ringColor: string
  glyph: string
}

/** Apparence d'une pastille : un problème reste rouge avec « ! », jamais signalé par la couleur seule. */
export function markerAppearance(marker: Pick<FieldMapMarker, 'status' | 'emphasis'>): MarkerAppearance {
  if (marker.status === 'PROBLEM') return { fill: colors.problem, ring: 'solid', ringColor: '#FFFFFF', glyph: '!' }
  switch (marker.emphasis) {
    case 'ACTIVE':
      return { fill: colors.text, ring: 'solid', ringColor: colors.surface, glyph: '' }
    case 'APPROACH':
      return { fill: colors.waiting, ring: 'dashed', ringColor: '#FFFFFF', glyph: '' }
    case 'CANDIDATE':
      return { fill: colors.text, ring: 'dashed', ringColor: colors.waiting, glyph: '?' }
    case null:
      return { fill: colors.waiting, ring: 'solid', ringColor: '#FFFFFF', glyph: '' }
  }
}

export interface FieldMapLayer {
  update: (
    markers: FieldMapMarker[],
    selectedId: string | null,
    operator: LatLngLiteral | null,
    /** Rotation de la carte (degrés) : les étiquettes se contre-tournent pour rester droites. */
    mapRotation?: number,
    /** Précision annoncée (m) quand elle bloque la détection : halo d'incertitude autour du tracteur. */
    uncertaintyM?: number | null,
    /** Étiquette du symbole (« Vous », « Vous · vue stabilisée »). */
    operatorLabel?: string,
  ) => void
  /** Taille des numéros civiques (paramètre de la tablette). */
  setCivicSize: (size: 'normal' | 'large') => void
  destroy: () => void
}

/** Échelle de travail : au-dessous, les repères proches se regroupent (60 px). */
export const workingZoom = 16
const clusterDistancePx = 60


function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  style: Partial<CSSStyleDeclaration>,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  Object.assign(node.style, style)
  if (text !== undefined) node.textContent = text
  return node
}

function pinNode(marker: FieldMapMarker, size: number): HTMLElement {
  const look = markerAppearance(marker)
  const pin = el('span', {
    width: `${String(size)}px`,
    height: `${String(size)}px`,
    flex: 'none',
    borderRadius: '50%',
    background: look.fill,
    border: `3px ${look.ring} ${look.ringColor}`,
    boxShadow: '0 1px 5px rgba(0,0,0,.55)',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0B0F1A',
    fontWeight: '800',
    fontSize: '14px',
  })
  if (look.glyph) pin.textContent = look.glyph
  return pin
}

export function createFieldMapLayer(
  api: GoogleMapsApi,
  map: GoogleMapInstance,
  onOpen: (itemId: string) => void,
): FieldMapLayer {
  let markers: FieldMapMarker[] = []
  let selectedId: string | null = null
  let operator: LatLngLiteral | null = null
  let rotation = 0
  let uncertainty: number | null = null
  let operatorLabel = 'Vous'
  let civicSize: 'normal' | 'large' = 'normal'
  let container: HTMLElement | null = null

  class Layer extends api.OverlayView implements OverlayViewBase {
    override onAdd() {
      container = el('div', { position: 'absolute', left: '0', top: '0' })
      this.getPanes()?.overlayMouseTarget.append(container)
    }

    override draw() {
      render(this)
    }

    override onRemove() {
      container?.remove()
      container = null
    }
  }

  const layer = new Layer()

  function zoomTo(ids: string[]) {
    const group = markers.filter((marker) => ids.includes(marker.id))
    if (group.length === 0) return
    const lats = group.map((marker) => marker.position.lat)
    const lngs = group.map((marker) => marker.position.lng)
    const north = Math.max(...lats)
    const south = Math.min(...lats)
    const east = Math.max(...lngs)
    const west = Math.min(...lngs)
    if (north === south && east === west) {
      map.panTo({ lat: north, lng: east })
      map.setZoom(workingZoom + 1)
      return
    }
    map.fitBounds({ north, south, east, west }, 80)
  }

  function openButton(marker: FieldMapMarker, content: HTMLElement[], extra: Partial<CSSStyleDeclaration>) {
    const button = el('button', {
      position: 'absolute',
      background: 'transparent',
      border: '0',
      padding: '0',
      cursor: 'pointer',
      font: 'inherit',
    })
    Object.assign(button.style, extra)
    button.type = 'button'
    button.setAttribute('aria-label', `${marker.address}${marker.name ? ` — ${marker.name}` : ''}`)
    button.append(...content)
    button.addEventListener('click', () => {
      onOpen(marker.id)
    })
    button.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
    })
    return button
  }

  function render(source: OverlayViewBase) {
    const projection = source.getProjection()
    if (!container || !projection) return
    container.replaceChildren()
    const bounds = map.getBounds()
    const zoom = map.getZoom() ?? workingZoom
    const visible = markers.filter((marker) => (bounds ? bounds.contains(marker.position) : true))
    const placed = visible.flatMap((marker) => {
      const point = projection.fromLatLngToDivPixel(marker.position)
      return point ? [{ marker, point }] : []
    })

    if (zoom >= workingZoom) {
      // Étiquettes : active, puis sélectionnée, puis ordre de la route; une étiquette qui en chevaucherait une autre
      // est omise (la pastille reste). Les étiquettes restent droites (contre-rotation de la carte).
      const priority = (marker: FieldMapMarker) =>
        marker.emphasis === 'ACTIVE' ? 0 : marker.id === selectedId ? 1 : 2 + (marker.order ?? 9999) / 10_000
      const ordered = [...placed].sort((a, b) => priority(a.marker) - priority(b.marker))
      const big = civicSize === 'large' ? 30 : 24
      const kept = placeLabels(
        ordered.map(({ marker, point }) => {
          const width = Math.max(90, Math.min(230, marker.address.length * 8)) + (marker.civic ? marker.civic.length * big * 0.6 : 0)
          return { id: marker.id, x: point.x + 16, y: point.y - 28, width, height: 56 }
        }),
      )
      for (const { marker, point } of placed) {
        const selected = marker.id === selectedId
        const active = marker.emphasis === 'ACTIVE'
        const content: HTMLElement[] = [pinNode(marker, active ? 38 : 32)]
        if (kept.has(marker.id)) {
          const label = el('span', {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            maxWidth: '300px',
            padding: '4px 10px 5px',
            borderRadius: '8px',
            background: colors.surface,
            boxShadow: selected || active ? '0 0 0 3px #000, 0 1px 6px rgba(0,0,0,.45)' : '0 1px 6px rgba(0,0,0,.45)',
            textAlign: 'left',
            transform: rotation === 0 ? '' : `rotate(${String(rotation)}deg)`,
            transformOrigin: 'left center',
          })
          if (marker.civic) {
            label.append(
              el(
                'span',
                {
                  fontSize: `${String(active ? big + 6 : big)}px`,
                  fontWeight: '900',
                  lineHeight: '1',
                  color: colors.text,
                  letterSpacing: '0.01em',
                },
                marker.civic,
              ),
            )
          }
          const text = el('span', { display: 'flex', flexDirection: 'column', minWidth: '0' })
          text.append(
            el(
              'span',
              { fontSize: '14px', fontWeight: '800', lineHeight: '1.2', color: colors.text },
              marker.chip ? `${marker.address} · ${marker.chip}` : marker.address,
            ),
            el(
              'span',
              {
                fontSize: '12.5px',
                fontWeight: '500',
                lineHeight: '1.25',
                color: colors.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              marker.name,
            ),
          )
          label.append(text)
          content.push(label)
        }
        container.append(
          openButton(marker, content, {
            left: `${String(point.x)}px`,
            top: `${String(point.y)}px`,
            transform: `translate(-${String(active ? 19 : 16)}px, -50%)`,
            width: 'max-content',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: '52px',
            zIndex: active ? '3' : selected ? '2' : '1',
          }),
        )
      }
    } else {
      const clusters = clusterPoints(
        placed.map(({ marker, point }) => ({ id: marker.id, x: point.x, y: point.y })),
        clusterDistancePx,
      )
      for (const cluster of clusters) {
        const only =
          cluster.ids.length === 1 ? markers.find((marker) => marker.id === cluster.ids[0]) : undefined
        if (only) {
          container.append(
            openButton(only, [pinNode(only, 26)], {
              left: `${String(cluster.x)}px`,
              top: `${String(cluster.y)}px`,
              transform: 'translate(-24px, -24px)',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }),
          )
          continue
        }
        const bubble = el(
          'button',
          {
            position: 'absolute',
            left: `${String(cluster.x)}px`,
            top: `${String(cluster.y)}px`,
            transform: `translate(-28px, -28px) rotate(${String(rotation)}deg)`,
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: colors.surface,
            border: `3px solid ${cluster.ids.some((id) => markers.find((marker) => marker.id === id)?.status === 'PROBLEM') ? colors.problem : colors.waiting}`,
            color: colors.text,
            font: 'inherit',
            fontSize: '18px',
            fontWeight: '800',
            cursor: 'pointer',
          },
          String(cluster.ids.length),
        )
        bubble.type = 'button'
        bubble.setAttribute('aria-label', `${String(cluster.ids.length)} résidences regroupées — zoomer`)
        bubble.addEventListener('click', () => {
          zoomTo(cluster.ids)
        })
        bubble.addEventListener('pointerdown', (event) => {
          event.stopPropagation()
        })
        container.append(bubble)
      }
    }

    if (operator) {
      const point = projection.fromLatLngToDivPixel(operator)
      // Position incertaine : la zone affichée est la précision annoncée, aucune adresse n'en est déduite.
      const edge =
        uncertainty === null ? null : projection.fromLatLngToDivPixel({ lat: operator.lat + uncertainty / 111_320, lng: operator.lng })
      if (point && edge) {
        const radius = Math.max(28, Math.abs(point.y - edge.y))
        container.append(
          el('div', {
            position: 'absolute',
            left: `${String(point.x - radius)}px`,
            top: `${String(point.y - radius)}px`,
            width: `${String(radius * 2)}px`,
            height: `${String(radius * 2)}px`,
            borderRadius: '50%',
            border: `2px dashed ${colors.waiting}`,
            background: 'rgba(245,184,46,.12)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }),
        )
      }
      if (point) {
        const halo = el('div', {
          position: 'absolute',
          left: `${String(point.x)}px`,
          top: `${String(point.y)}px`,
          transform: 'translate(-28px, -28px)',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'rgba(26,102,217,.28)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        })
        halo.setAttribute('aria-label', 'Votre position')
        halo.append(
          el('div', {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: colors.operator,
            border: '3px solid #FFFFFF',
            boxShadow: '0 1px 4px rgba(0,0,0,.4)',
          }),
          el(
            'div',
            {
              position: 'absolute',
              left: '46px',
              top: '14px',
              height: '26px',
              padding: '0 9px',
              borderRadius: '13px',
              background: colors.operator,
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '800',
              display: 'flex',
              alignItems: 'center',
            },
            operatorLabel,
          ),
        )
        container.append(halo)
      }
    }
  }

  layer.setMap(map)
  const idle = map.addListener('idle', () => {
    layer.draw()
  })

  return {
    update(nextMarkers, nextSelectedId, nextOperator, nextRotation = 0, nextUncertainty = null, nextLabel = 'Vous') {
      operatorLabel = nextLabel
      rotation = nextRotation
      uncertainty = nextUncertainty
      markers = nextMarkers
      selectedId = nextSelectedId
      operator = nextOperator
      layer.draw()
    },
    setCivicSize(size) {
      civicSize = size
      layer.draw()
    },
    destroy() {
      idle.remove()
      layer.setMap(null)
    },
  }
}
